import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { listCategoriesWithCounts } from "@/lib/catalog.functions";
import { topCategories } from "@/data/topCategories";

import {
  Gem, Shirt, Home, Sparkles, Baby, Gift, Flower2, Dog, UtensilsCrossed, Armchair,
  Radio, Image as ImageIcon, Hammer, Link2, Circle, Ear, CircleDot, Droplets, HeartPulse,
  Bath, User, UserRound, Package, Briefcase, Mail, ToyBrick, Scissors, Palette,
  Grid3x3, Watch, ShoppingBag, Tag,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import pageCover from "@/assets/kupish/p-kategorije.webp";

const ICONS: Record<string, LucideIcon> = {
  "nakit": Gem,
  "moda": Shirt,
  "dom-ambient": Home,
  "naravna-kozmetika": Sparkles,
  "otroci": Baby,
  "obdarovanje": Gift,
  "dekoracija": Flower2,
  "hisni-ljubljencki": Dog,
  "kuhinja": UtensilsCrossed,
  "pohistvo": Armchair,
  "retro-dekor": Radio,
  "slike": ImageIcon,
  "starejsa-obrt": Hammer,
  "obeski-ogrlice": Link2,
  "prstani": Circle,
  "uhani": Ear,
  "zapestnice": CircleDot,
  "kopalne-soli": Droplets,
  "mazila-kreme": HeartPulse,
  "naravna-mila": Bath,
  "darila-za-moske": User,
  "darila-za-zenske": UserRound,
  "darilne-skatle": Package,
  "poslovna-darila": Briefcase,
  "voscilnice": Mail,
  "darila-za-otroke": ToyBrick,
  "igrace": ToyBrick,
  "oblacila-otroci": Baby,
  "klekljanje": Scissors,
  "kvackanje": Palette,
  "moda-dodatki": Watch,
  "oblacila": Shirt,
  "prticki": Grid3x3,
  "strikanje": Palette,
  "tkanje": Grid3x3,
  "torbice": ShoppingBag,
};

export const Route = createFileRoute("/kategorije/")({
  head: () => {
    const url = "https://kupish.si/kategorije";
    const title = "Kategorije — kupish.si";
    const description = "Brskajte nove izdelke preverjenih ponudnikov po kategorijah: elektronika, dom in vrt, avto, moda, šport, drogerija in več.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: title,
            description: "Imenik kategorij izdelkov v spletnem nakupovalnem središču kupish.si.",
            url,
            inLanguage: "sl-SI",
            isPartOf: { "@type": "WebSite", name: "kupish.si", url: "https://kupish.si/" },
            mainEntity: {
              "@type": "ItemList",
              name: "Kategorije izdelkov",
              numberOfItems: topCategories.length,
              itemListElement: topCategories.map((c, i) => ({
                "@type": "ListItem",
                position: i + 1,
                name: c.name,
                url: `https://kupish.si/kategorije/${c.slug}`,
              })),
            },
          }),
        },

        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Domov", item: "https://kupish.si/" },
              { "@type": "ListItem", position: 2, name: "Kategorije", item: url },
            ],
          }),
        },
      ],
    };
  },
  component: Page,
});

function Page() {
  const fetchCats = useServerFn(listCategoriesWithCounts);
  const { data, isLoading } = useQuery({ queryKey: ["cats-counts"], queryFn: () => fetchCats() });
  const cats = data?.categories ?? [];

  return (
    <section className="pb-10 md:pb-16">
      <PageHero src={pageCover} alt="Kategorije izdelkov na kupish.si — minimalističen 3D prikaz mreže kategorij">
        <div className="eyebrow">Nakupovalno središče</div>
        <h1 className="mt-3 font-serif text-4xl leading-tight md:text-6xl">Kategorije</h1>
        <p className="mt-3 max-w-xl text-sm text-muted-foreground md:mt-4 md:text-base">Izbrane skupine izdelkov različnih ponudnikov na enem mestu.</p>
      </PageHero>
      <div className="mx-auto max-w-7xl px-4 md:px-6">

      {isLoading ? (
        <div className="mt-10 text-muted-foreground md:mt-16">Nalagam…</div>
      ) : cats.length === 0 ? (
        <div className="mt-10 text-muted-foreground md:mt-16">Ni kategorij.</div>
      ) : (
        <div className="mt-8 grid grid-cols-1 gap-px border border-border bg-border sm:grid-cols-2 md:mt-12 md:grid-cols-3">
          {cats.map((c) => {
            const Icon = ICONS[c.slug] ?? Tag;
            return (
              <Link key={c.id} to="/kategorije/$slug" params={{ slug: c.slug }}
                className="group bg-background p-5 transition-colors hover:bg-muted/40 md:p-8">
                <div className="flex items-start gap-3 md:gap-4">
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted/60 text-brand transition-colors group-hover:bg-accent/10 group-hover:text-accent md:h-12 md:w-12">
                    <Icon className="h-5 w-5 md:h-6 md:w-6" strokeWidth={1.5} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h2 className="font-serif text-xl leading-snug transition-colors group-hover:text-accent md:text-2xl">{c.name}</h2>
                    {c.description && <p className="mt-1.5 text-sm text-muted-foreground line-clamp-2 md:mt-2">{c.description}</p>}
                    <div className="mt-3 eyebrow md:mt-4">{c.product_count} izdelkov →</div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
      </div>
    </section>
  );
}
